import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { SitterCard } from 'us-react-components/dist/SitterCard/SitterCard';
import { SitterCardSkeleton } from 'us-react-components/dist/SitterCard/SitterCardSkeleton';
import { Sitter } from 'us-web-services';
import { useDependencies } from '../../../../../dependencies';
import { Routes } from '../../../../../Routes';
import { MoreResultsButton } from '../../../../Button';
import { BestMatchNullState } from './BestMatchNullState';

type Props = {
  sitters: Sitter[];
  loading?: boolean;
  hideExperience: boolean;
  handleClickViewResults(bookingVerticalTypeId: number | undefined): void;
  handlePostAJob(): void;
  setFavorite(isFavorited: boolean, favoriteId: number, sitterId: number): Promise<void>;
}

const SKELETONS = [
  <SitterCardSkeleton key='skeleton-1' />,
  <SitterCardSkeleton key='skeleton-2' />,
  <SitterCardSkeleton key='skeleton-3' />,
  <SitterCardSkeleton key='skeleton-4' />,
];

export const BestMatch = (props: Props): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { services: { tracker } , stores } = useDependencies();

  const {
    sitters,
    hideExperience = false,
    setFavorite,
    handleClickViewResults,
    handlePostAJob,
    loading,
  } = props;

  const handleClick = (id: number): void => {
    const route = generatePath(Routes.sitter.profile, { id });
    navigate(route);
  }

  const trackEvent = async (event: string, params?: {elementText: string}): Promise<void> => tracker.track(event, params);

  const showSitters = sitters.slice(0, 4);

  return (
    <div className='grid bg-transparent mb-8 min-h-[316px]'>
      <div className='block font-bold mb-2.5'>{t('dashboard.widgets.bestMatch.label')}</div>
      {!sitters.length && !loading ? (
        <BestMatchNullState
          handlePostAJob={handlePostAJob}
          header={t('dashboard.widgets.bestMatch.nullStateText1')}
          content={t('dashboard.widgets.bestMatch.nullStateText2')}
        />
      ) : (
        <>
          <div className='grid grid-cols-2 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 mb-8 gap-x-4 gap-y-8'>
            {loading ? SKELETONS : showSitters.map(sitter => (
              <div
                className='m-b-10'
                key={`${sitter.firstName}-${sitter.id}`}>
                <SitterCard
                  sitter={sitter}
                  numberOfChildren={stores.parent.getNumberOfChildren()}
                  handleClick={handleClick}
                  trackingCallback={trackEvent}
                  hideExperience={hideExperience}
                  setFavorite={setFavorite}
                  loading={loading}
                />
              </div>
            ))}
          </div>
          <MoreResultsButton
            handleClickViewResults={handleClickViewResults}
          />
        </>
      )}
    </div>
  );
};
